import { AnimatePresence, motion } from "framer-motion";
import type { Ref } from "react";
import {
  formatLeaderboardMetricValue,
  type DisplayLeaderboardRow
} from "./leaderboardTableModel";

type LeaderboardTableProps = {
  metricLabel: string;
  rows: readonly DisplayLeaderboardRow[];
  isLoading: boolean;
  errorMessage: string | null;
  transitionKey: string;
  onRetry?: () => void;
  onRequestChangeName?: () => void;
  loadingMessage?: string;
  emptyMessage?: string;
  headClassName?: string;
  viewportClassName?: string;
  tableClassName?: string;
  statusClassName?: string;
  rowClassName?: string;
  viewportRef?: Ref<HTMLDivElement>;
  rowAnimationDelayStep?: number;
};

function getClassName(baseClassName: string, extraClassName?: string): string {
  return extraClassName === undefined ? baseClassName : `${baseClassName} ${extraClassName}`;
}

export function LeaderboardTable({
  metricLabel,
  rows,
  isLoading,
  errorMessage,
  transitionKey,
  onRetry,
  onRequestChangeName,
  loadingMessage = "Loading scores...",
  emptyMessage = "No scores yet.",
  headClassName,
  viewportClassName,
  tableClassName,
  statusClassName,
  rowClassName,
  viewportRef,
  rowAnimationDelayStep = 0.05
}: LeaderboardTableProps) {
  return (
    <>
      <div className={getClassName("leaderboard-table-head", headClassName)}>
        <span>RANK</span>
        <span>PLAYER</span>
        <span>{metricLabel}</span>
      </div>

      <div className={getClassName("leaderboard-table-viewport", viewportClassName)} ref={viewportRef}>
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key={`${transitionKey}-loading`}
              className={getClassName("leaderboard-status", statusClassName)}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <p className="leaderboard-status-message" aria-live="polite">
                {loadingMessage}
              </p>
            </motion.div>
          ) : errorMessage !== null ? (
            <motion.div
              key={`${transitionKey}-error`}
              className={getClassName("leaderboard-status", statusClassName)}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <p className="leaderboard-status-message leaderboard-status-message-error" role="alert">
                {errorMessage}
              </p>
              {onRetry !== undefined ? (
                <button
                  type="button"
                  className="leaderboard-status-button"
                  onClick={onRetry}
                >
                  RETRY
                </button>
              ) : null}
            </motion.div>
          ) : rows.length === 0 ? (
            <motion.div
              key={`${transitionKey}-empty`}
              className={getClassName("leaderboard-status", statusClassName)}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <p className="leaderboard-status-message">{emptyMessage}</p>
            </motion.div>
          ) : (
            <motion.ol
              key={transitionKey}
              className={getClassName("leaderboard-table", tableClassName)}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {rows.map((row, index) => (
                <motion.li
                  key={row.key}
                  data-entry-id={row.entryId ?? undefined}
                  className={`leaderboard-row${row.isCurrentPlayer ? " leaderboard-row-current" : ""}${
                    row.isPlaceholder ? " leaderboard-row-placeholder" : ""
                  }${rowClassName === undefined ? "" : ` ${rowClassName}`}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.24,
                    delay: Math.min(index, 6) * rowAnimationDelayStep,
                    ease: "easeOut"
                  }}
                >
                  <span className="leaderboard-rank">{row.rank}</span>
                  <span className="leaderboard-name-shell">
                    <span className="leaderboard-name">{row.name}</span>
                    {row.showChangeNameButton && onRequestChangeName !== undefined ? (
                      <button
                        type="button"
                        className="leaderboard-change-name-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRequestChangeName();
                        }}
                      >
                        Change Name
                      </button>
                    ) : null}
                  </span>
                  <span className="leaderboard-value">{formatLeaderboardMetricValue(row.value)}</span>
                </motion.li>
              ))}
            </motion.ol>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
